import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from '@libs/ui-lib';
import { PatientAlertV2, ProvisionalStatus } from '@ffd/api-client';
import { DateTime } from 'luxon';

export type PatientAlertDateState = 'alwaysOn' | 'notStarted' | 'active' | 'ended';

const DATE_FORMAT = 'LL/dd/yyyy';

@Component({
  selector: 'lib-patient-alerts-date-indicator',
  standalone: true,
  imports: [CommonModule, TranslateModule, TooltipModule],
  templateUrl: './patient-alerts-date-indicator.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientAlertsDateIndicatorComponent {
  public readonly patientAlert = input.required<PatientAlertV2>();
  public readonly now = input<DateTime | null>(null);

  private readonly today = computed(() => (this.now() ?? DateTime.now()).startOf('day'));

  private readonly startDate = computed(() => {
    const v = this.patientAlert().startDate;
    return v ? DateTime.fromISO(v).startOf('day') : null;
  });

  private readonly endDate = computed(() => {
    const v = this.patientAlert().endDate;
    return v ? DateTime.fromISO(v).startOf('day') : null;
  });

  public readonly state = computed<PatientAlertDateState | null>(() => {
    const item = this.patientAlert();
    if (item.isAlways) {
      return 'alwaysOn';
    }
    const today = this.today();
    const start = this.startDate();
    const end = this.endDate();
    if (start && today < start) {
      return 'notStarted';
    }
    if (end && today >= end) {
      return 'ended';
    }
    if (start || end) {
      return 'active';
    }
    return null;
  });

  private readonly isReady = computed(() => (this.patientAlert().provisionalStatus ?? ProvisionalStatus.READY) === ProvisionalStatus.READY);

  public readonly visible = computed(() => this.isReady() && this.state() !== null);

  public readonly badgeLabelKey = computed<string>(() => {
    switch (this.state()) {
      case 'alwaysOn': {
        return 'health_history_patient_alerts_tab_always_on';
      }
      case 'notStarted': {
        return 'patient_alerts_state_starts_on';
      }
      case 'ended': {
        return 'patient_alerts_state_ended_on';
      }
      case 'active': {
        return this.endDate() ? 'patient_alerts_state_ends_on' : 'patient_alerts_state_started_on';
      }
      default: {
        return '';
      }
    }
  });

  public readonly badgeDate = computed<string>(() => {
    switch (this.state()) {
      case 'alwaysOn': {
        return '';
      }
      case 'notStarted': {
        return this.startDate()?.toFormat(DATE_FORMAT) ?? '';
      }
      case 'ended': {
        return this.endDate()?.toFormat(DATE_FORMAT) ?? '';
      }
      case 'active': {
        return (this.endDate() ?? this.startDate())?.toFormat(DATE_FORMAT) ?? '';
      }
      default: {
        return '';
      }
    }
  });

  public readonly createdDate = computed<string>(() => {
    const v = this.patientAlert().createDate;
    return v ? DateTime.fromISO(v).toFormat(DATE_FORMAT) : '';
  });

  private readonly formattedStartDate = computed<string>(() => this.startDate()?.toFormat(DATE_FORMAT) ?? '');
  private readonly formattedEndDate = computed<string>(() => this.endDate()?.toFormat(DATE_FORMAT) ?? '');

  public readonly startsDate = this.formattedStartDate;
  public readonly startedDate = this.formattedStartDate;
  public readonly endsDate = this.formattedEndDate;
  public readonly endedDate = this.formattedEndDate;

  public readonly showStartsRow = computed(() => this.state() === 'notStarted' && this.startDate() !== null);
  public readonly showStartedRow = computed(() => {
    const s = this.state();
    return (s === 'active' || s === 'ended') && this.startDate() !== null;
  });
  public readonly showEndsRow = computed(() => {
    const s = this.state();
    return (s === 'notStarted' || s === 'active') && this.endDate() !== null;
  });
  public readonly showEndedRow = computed(() => this.state() === 'ended' && this.endDate() !== null);
  public readonly showShowInRow = computed(() => this.patientAlert().showInSchedule || this.patientAlert().showInPatientChart);
  public readonly showInList = computed<string[]>(() => {
    const item = this.patientAlert();
    const keys: string[] = [];
    if (item.showInSchedule) {
      keys.push('health_history_patient_alerts_tab_schedule');
    }
    if (item.showInPatientChart) {
      keys.push('health_history_patient_alerts_tab_patient_chart');
    }
    return keys;
  });
  public readonly showPermanentRow = computed(() => this.state() === 'alwaysOn');

  protected readonly DATE_FORMAT = DATE_FORMAT;
}
